import React from "react";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import useAuth from "../hooks/useAuth";
import { resolvePostLoginPath } from "./postLoginRedirect";
import CalmAuthShell, {
  calmPanelSx,
  calmInputSx,
  calmPrimaryButtonSx,
} from "./CalmAuthShell";

import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  Collapse,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";
import {
  PersonOutline,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

const ink = "#2c4054";
const inkMuted = "#5a6e82";
const iconMuted = "rgba(44, 64, 84, 0.42)";

const LoginSchema = Yup.object().shape({
  identifier: Yup.string()
    .trim()
    .required("Email or username is required")
    .test(
      "email-or-username",
      "Enter a valid email or a username (at least 2 characters)",
      (value) => {
        if (!value) return false;
        if (value.includes("@")) {
          return Yup.string().email().isValidSync(value);
        }
        return value.trim().length >= 2;
      }
    ),
  password: Yup.string().trim().required("Password is required"),
});

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <CalmAuthShell maxWidth={420}>
      <Box component="section" sx={{ ...calmPanelSx, p: { xs: 3, sm: 4 } }}>
        <Box sx={{ textAlign: "center", mb: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.25,
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL || ""}/black_on_white.png`}
              alt="OnTheFarm Today"
              loading="lazy"
              sx={{
                display: "block",
                width: { xs: 130, sm: 164 },
                height: "auto",
                objectFit: "contain",
                filter:
                  "drop-shadow(0 1px 0 rgba(255,255,255,0.3)) drop-shadow(0 3px 10px rgba(26,42,58,0.28))",
              }}
            />
          </Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 600,
              letterSpacing: "-0.02em",
              color: ink,
              fontSize: "1.25rem",
            }}
          >
            Sign in
          </Typography>
          <Typography variant="body2" sx={{ color: inkMuted, mt: 0.75 }}>
            Continue to your farm workspace
          </Typography>
        </Box>

        <Formik
          initialValues={{ identifier: "", password: "", remember: true }}
          validationSchema={LoginSchema}
          validateOnMount
          onSubmit={async (
            values,
            { setSubmitting, setFieldError, setStatus }
          ) => {
            setStatus(undefined);
            const { remember, identifier, password } = values;

            try {
              const result = await login(
                { identifier: identifier.trim(), password },
                { remember }
              );
              if (result?.success) {
                navigate(
                  resolvePostLoginPath(result.user, location.state?.from),
                  { replace: true }
                );
                return;
              }
              const status = result?.status;
              const detail = result?.data?.detail || result?.message;
              if (status === 400 && detail === "Invalid credentials") {
                setFieldError(
                  "password",
                  "Invalid email, username, or password"
                );
              } else {
                setStatus({
                  serverError:
                    detail || "Unable to sign in. Please try again.",
                });
              }
            } catch (err) {
              const status = err?.response?.status;
              const detail = err?.response?.data?.detail;
              if (status === 400 && detail === "Invalid credentials") {
                setFieldError(
                  "password",
                  "Invalid email, username, or password"
                );
              } else if (status === 422) {
                setStatus({
                  serverError:
                    "Invalid request. Please check your input and try again.",
                });
              } else if (status >= 500) {
                setStatus({
                  serverError: "Server error. Please try again in a moment.",
                });
              } else {
                setStatus({
                  serverError:
                    detail ||
                    "Network error. Check your internet connection and try again.",
                });
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            isSubmitting,
            errors,
            touched,
            isValid,
            dirty,
            values,
            setFieldValue,
            status,
          }) => (
            <Form noValidate>
              {location.state?.passwordResetComplete && (
                <Alert
                  severity="success"
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderRadius: 1.5,
                    bgcolor: "rgba(255, 255, 255, 0.38)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}
                >
                  Password updated. You can sign in with your new password.
                </Alert>
              )}
              <Collapse in={Boolean(status?.serverError)} unmountOnExit>
                <Alert
                  severity="error"
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderRadius: 1.5,
                    bgcolor: "rgba(255, 255, 255, 0.36)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}
                >
                  {status?.serverError}
                </Alert>
              </Collapse>

              <Field
                as={TextField}
                name="identifier"
                label="Email or username"
                fullWidth
                margin="dense"
                size="small"
                autoComplete="username"
                onBlur={(e) => {
                  setFieldValue("identifier", e.target.value.trim());
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutline sx={{ color: iconMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1, ...calmInputSx }}
                error={touched.identifier && Boolean(errors.identifier)}
                helperText={touched.identifier && errors.identifier}
              />

              <Field
                as={TextField}
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="dense"
                size="small"
                autoComplete="current-password"
                onBlur={(e) =>
                  setFieldValue("password", e.target.value.trim())
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: iconMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((s) => !s)}
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ color: iconMuted, fontSize: 20 }} />
                        ) : (
                          <Visibility sx={{ color: iconMuted, fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1.5, ...calmInputSx }}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />

              <FormControlLabel
                sx={{ mt: 1, ml: -0.5, "& .MuiFormControlLabel-label": { fontSize: "0.875rem", color: inkMuted } }}
                control={
                  <Checkbox
                    size="small"
                    checked={values.remember}
                    onChange={(e) =>
                      setFieldValue("remember", e.target.checked)
                    }
                    sx={{ color: inkMuted, "&.Mui-checked": { color: "#4a7eb0" } }}
                  />
                }
                label="Remember me"
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disableElevation
                disabled={isSubmitting || !isValid || !dirty}
                sx={calmPrimaryButtonSx}
              >
                {isSubmitting ? "Signing in…" : "Sign in"}
              </Button>

              <Box
                sx={{
                  mt: 2.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  underline="hover"
                  sx={{ fontSize: "0.875rem", color: inkMuted }}
                >
                  Forgot password?
                </Link>
                <Typography variant="body2" sx={{ color: inkMuted, fontSize: "0.875rem" }}>
                  Need an account?{" "}
                  <Link component={RouterLink} to="/signup" underline="hover" sx={{ color: ink, fontWeight: 600 }}>
                    Sign up
                  </Link>
                </Typography>
              </Box>
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Link
                  component={RouterLink}
                  to="/"
                  underline="hover"
                  sx={{ fontSize: "0.8125rem", color: inkMuted }}
                >
                  About OnTheFarm Today
                </Link>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </CalmAuthShell>
  );
}
